import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router'
import useRouter from 'utils/useRouter'
import { useFormState } from 'hooks/useFormState'
import { useVestigioService } from 'hooks/useVestigioService'
import { usePericiandoService } from 'hooks/usePericiandoService'
import { useVeiculoService } from 'hooks/useVeiculoService'
import { useAuth } from 'hooks/useAuth'

const schema = {
  lacre: {
    presence: { allowEmpty: false, message: '^Lacre é obrigatório' },
  },
  tipoEmbalagem: {
    presence: { allowEmpty: false, message: '^Tipo de embalagem é obrigatório' },
  },
  tiposGens: {
    presence: { allowEmpty: false, message: '^Natureza genérica é obrigatório' },
  },
  tiposEsps: {
    presence: { allowEmpty: false, message: '^Natureza específica é obrigatório' },
  },
  descricao: {
    presence: { allowEmpty: false, message: '^A Descrição do vestígio é obrigatória' },
  },
  setorAtual: {
    presence: { allowEmpty: false, message: '^Setor inicial do novo vestígio é obrigatório' },
  },
}

export const useExtracaoVestigio = ({ route }) => {
  const router = useRouter()
  const params = useParams()
  const { currentUser } = useAuth()
  const formExtracao = useFormState(schema, {
    lacre: null,
    tipoEmbalagem: null,
    outroTipoEmbalagem: null,
    tiposGens: null,
    tiposEsps: null,
    tamanhoObjeto: null,
    descricao: null,
    contraprova: null,
    setorAtual: null,
  })

  const vestigioService = useVestigioService()
  const periciandoService = usePericiandoService()
  const veiculoService = useVeiculoService()

  const [processando, setProcessando] = useState(false)
  const [tiposVestigios, setTiposVestigios] = useState([])
  const [processandoTiposVestigios, setProcessandoTiposVestigios] = useState(false)
  const [tiposVestigiosEspecificos, setTiposVestigiosEspecificos] = useState([])
  const [processandoTiposVestigiosEspecificos, setProcessandoTiposVestigiosEspecificos] = useState(false)
  const [opcoesTiposVestigiosEspecificosAtual, setOpcoesTiposVestigiosEspecificosAtual] = useState([])
  const [vestigio, setVestigio] = useState(null)
  const [casosDoObjetoOrigem, setCasosDoObjetoOrigem] = useState([])

  const fetchTiposVestigios = useCallback(async () => {
    const response = await vestigioService.listarTiposVestigiosAsync(setProcessandoTiposVestigios)
    setTiposVestigios(response)
  }, [])

  const fetchVestigio = useCallback(async () => {
    if (params.id && route.editar) {
      const response = await vestigioService.listarVestigioDetalhado(params.id)
      setVestigio(response)
    }
  }, [params.id, route.editar])

  const fetchCasosDoObjetoOrigem = useCallback(async () => {
    if (route.origem === 'VESTIGIO') {
      const response = await vestigioService.listarCasosDeVestigio(params.id)
      setCasosDoObjetoOrigem(response)
    } else if (route.origem === 'VEICULO') {
      const response = await veiculoService.listarCasosDeVeiculo(params.id)
      setCasosDoObjetoOrigem(response)
    } else if (route.origem === 'PERICIANDO') {
      const response = await periciandoService.listarCasosDePericiando(params.id)
      setCasosDoObjetoOrigem(response)
    }
  }, [params.id, route.origem])

  const fetchTiposVestigiosEspecificos = useCallback(async () => {
    const response = await vestigioService.listarTiposVestigiosEspecificosAsync(setProcessandoTiposVestigiosEspecificos)
    setTiposVestigiosEspecificos(response)
  }, [])

  useEffect(() => {
    fetchTiposVestigios()
  }, [fetchTiposVestigios])

  useEffect(() => {
    fetchTiposVestigiosEspecificos()
  }, [fetchTiposVestigiosEspecificos])

  useEffect(() => {
    fetchVestigio()
  }, [fetchVestigio])

  useEffect(() => {
    fetchCasosDoObjetoOrigem()
  }, [fetchCasosDoObjetoOrigem])

  useEffect(() => {
    if (vestigio?.id && route.editar) {
      formExtracao.setFormState((prevState) => ({
        ...prevState,
        values: {
          ...prevState.values,
          lacre: vestigio.lacre,
          tipoEmbalagem: vestigio.tipoEmbalagem,
          outroTipoEmbalagem: vestigio.outroTipoEmbalagem,
          tiposGens: vestigio.tiposGens,
          tiposEsps: vestigio.tiposEsps,
          tamanhoObjeto: vestigio.tamanhoObjeto,
          descricao: vestigio.descricao,
          contraprova: vestigio.contraprova,
          setorAtual: vestigio.setAtual,
        },
      }))
    }
  }, [vestigio, route])

  const formError = useCallback(() => {
    if (
      !formExtracao.formState.values.lacre ||
      !formExtracao.formState.values.tipoEmbalagem ||
      !formExtracao.formState.values.tiposGens ||
      !formExtracao.formState.values.tiposEsps ||
      !formExtracao.formState.values.descricao ||
      !formExtracao.formState.values.setorAtual ||
      (route.origem != 'CADAVER' && !route.editar && !formExtracao.formState.values.caso)
    ) {
      return true
    }
    return false
  }, [formExtracao.formState.values, route])

  const handleChangeTipoVestigioGenerica = useCallback(
    (event, newValue) => {
      formExtracao.handleChangeNewValueByNameProperty(event, newValue, 'tiposGens')
      const opcoesTiposEspecificos = new Set()

      tiposVestigiosEspecificos.forEach((tipoEspecifico) => {
        tipoEspecifico.tiposGens?.forEach((tipoGenerico) => {
          newValue.forEach((novoValor) => {
            if (novoValor.identificador === tipoGenerico.identificador) {
              opcoesTiposEspecificos.add(tipoEspecifico)
            }
          })
        })
      })

      setOpcoesTiposVestigiosEspecificosAtual([...opcoesTiposEspecificos])
    },
    [tiposVestigiosEspecificos]
  )

  const handleConfirmarExtracao = async () => {
    if (!formError()) {
      let novoVestigio = null
      if (route.origem === 'CADAVER') {
        novoVestigio = await vestigioService.extrairVestigioDeCadaver(params.id, formExtracao.formState.values, setProcessando)
      }

      if (route.origem === 'VESTIGIO') {
        novoVestigio = await vestigioService.extrairVestigioDeVestigio(params.id, formExtracao.formState.values, setProcessando)
      }

      if (route.origem === 'VEICULO') {
        novoVestigio = await vestigioService.extrairVestigioDeVeiculo(params.id, formExtracao.formState.values, setProcessando)
      }

      if (route.origem === 'PERICIANDO') {
        novoVestigio = await vestigioService.extrairVestigioDePericiando(params.id, formExtracao.formState.values, setProcessando)
      }

      if (novoVestigio) {
        router.history.push(`/casos/caso/vestigio/detalhar/${novoVestigio.id}`)
      }
    }
  }

  const handleEditarVestigio = async () => {
    try {
      const dados = {
        tiposGens: formExtracao.formState.values.tiposGens,
        tiposEsps: formExtracao.formState.values.tiposEsps,
        lacre: formExtracao.formState.values.lacre,
        descricao: formExtracao.formState.values.descricao,
        tipoEmbalagem: formExtracao.formState.values.tipoEmbalagem,
        outroTipoEmbalagem: formExtracao.formState.values.outroTipoEmbalagem,
        tamanhoObjeto: formExtracao.formState.values.tamanhoObjeto,
        id_setor_atual: formExtracao.formState.values.setorAtual.id,
        contraprova: formExtracao.formState.values.contraprova,
      }
      await vestigioService.editarVestigioDeExtracao(params.id, dados, setProcessando)
      router.history.push(`/casos/caso/vestigio/detalhar/${params.id}`)
    } catch (err) {
      throw err
    }
  }

  return {
    formExtracao,
    processando,
    tiposVestigios,
    processandoTiposVestigios,
    processandoTiposVestigiosEspecificos,
    opcoesTiposVestigiosEspecificosAtual,
    casosDoObjetoOrigem,
    currentUser,
    formError,
    handleChangeTipoVestigioGenerica,
    handleConfirmarExtracao,
    handleEditarVestigio,
    router,
  }
}
